import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import * as SystemActions from '../../../store/system/actions/system.actions';
import * as AuthActions from '../../../store/auth/actions/auth.actions';
import { AuthService } from 'src/app/services/http/auth.service';
import { AuthFacade } from 'src/app/store/auth/facade/auth.facade';
import { TwoFactorAuthenticationComponent } from '../two-factor-authentication/two-factor-authentication.component';
import { getSignInState } from 'src/app/store/auth/selectors/auth.selectors';
import { ISignInState } from 'src/app/store/state/auth.signin.state';
import { IAppState } from 'src/app/store/state/app.state';
import { AppConfig } from 'src/app/app.config';
import { Features } from '../../../models/features';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UtilitiesService } from 'src/app/services/http/utilities.service';
import { IJwtoken } from "../../../models/jwtoken";
import { finalize } from 'rxjs/operators';
import { ILinkSettings } from 'src/app/models/app-config.model';
import { IEnvironmentConfig, ICognitoSettings } from 'src/app/models/environmentConfig';
@UntilDestroy()
@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss']
})
export class SigninComponent implements OnInit {
  signinForm: UntypedFormGroup;
  email: UntypedFormControl;
  password: UntypedFormControl;

  hide = true;
  authFailText = '';
  showSpinner = false;
  showContactUs: boolean;
  contactUsLink: string;
  contactUsHtml: string;
  contactUsForRegistrationLink: string;
  displayRegister = false;
  showSendEmailForRegistration = false;

  signInWelcomeText: string;
  signInWelcomeSecondaryText: string;
  loginButtonText: string;
  ssoLoginButtonText: string;
  showCreateNewAccountText: boolean;
  createNewAccountText: string;
  createNewAccountLink: string;
  createNewAccountLinkText: string;

  enableAwsCognitoLogin: boolean = false;

  footerHtml: string;

  get envSettings(): IEnvironmentConfig {
    return this.appConfig.getEnvironmentConfig();
  }

  get cognitoSettings(): ICognitoSettings {
    return this.appConfig.getCognitoSettings();
  }

  constructor(
    private store: Store<IAppState>,
    private authService: AuthService,
    private dialog: MatDialog,
    private appConfig: AppConfig,
    private utilitiesService: UtilitiesService,
    private authFacade: AuthFacade
  ) {
  }

  ngOnInit() {
    this.initializeFormControls();
    this.initializeCognitoLoginSetting();
    this.subscribeToSignInState();
    this.loadSignInConfiguration();
    this.checkForAuthorizationCode();
  }

  private initializeFormControls() {
    this.email = new UntypedFormControl(null, [Validators.required, Validators.email]);
    this.password = new UntypedFormControl(null, Validators.required);
    this.signinForm = new UntypedFormGroup({
      email: this.email,
      password: this.password
    });
  }

  private initializeCognitoLoginSetting() {
    this.enableAwsCognitoLogin = this.cognitoSettings?.ssoEnabled ?? false;
  }

  private subscribeToSignInState() {
    this.store.pipe(
      untilDestroyed(this),
      select(getSignInState)
    ).subscribe((state: ISignInState) => {
      this.enableDisableForm(this.signinForm, !state.loading);
      this.showSpinner = state.loading;

      if (state.requireTFA && !state.loading) {
        this.dialog.open(TwoFactorAuthenticationComponent)
          .afterClosed().subscribe(tfaCode => {
          if (tfaCode) {
            this.store.dispatch(new AuthActions.AuthenticateTFA(this.email.value, this.password.value, tfaCode));
          }
        });
      }
    });
  }

  private loadSignInConfiguration() {
    const links: ILinkSettings = this.appConfig.getLinks();
    let signInConfig = this.appConfig.getSection(Features.SIGN_IN) || {};
    
    this.showContactUs = (signInConfig.showContactUsText ?? true) && !!links.contactUs;
    this.contactUsLink = links.contactUs;
    this.contactUsHtml = (signInConfig.contactUsText || signInConfig.contactUsHtml) ?? "Having trouble logging in or need to change your details?";
    this.displayRegister = (this.appConfig.getSection(Features.REGISTRATION)) ? this.appConfig.getSection(Features.REGISTRATION).showRegisterLinkOnLogin : false;
    this.showSendEmailForRegistration = (this.appConfig.getSection(Features.REGISTRATION)) ? this.appConfig.getSection(Features.REGISTRATION).showEmailToRegisterOnLogin : false;
    this.contactUsForRegistrationLink = (this.showSendEmailForRegistration) ? links.contactUsForRegistration : "";

    this.signInWelcomeText = signInConfig.loginWelcomeText || "Welcome back";
    this.signInWelcomeSecondaryText = signInConfig.loginWelcomeSecondaryText || "";
    this.loginButtonText = signInConfig.loginButtonText || "Login";
    this.ssoLoginButtonText = this.cognitoSettings?.ssoLoginButtonText ?? "SSO Login";
    this.showCreateNewAccountText = !!signInConfig.showCreateNewAccountText;
    this.createNewAccountText = signInConfig.createNewAccountText || "";
    this.createNewAccountLink = signInConfig.createNewAccountLink || "";
    this.createNewAccountLinkText = signInConfig.createNewAccountLinkText || "";

    this.footerHtml = signInConfig.footerHtml || "";

    this.authFacade.getAuthFailText()
      .pipe(untilDestroyed(this))
      .subscribe((failText: string) => {
        this.authFailText = failText;
      });
  }

  private checkForAuthorizationCode() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (this.enableAwsCognitoLogin && code) {
      this.exchangeCodeForTokens(code);
    }
  }

  private enableDisableForm(form: UntypedFormGroup, enable: boolean) {
    if (enable) {
      form.enable();
    } else {
      form.disable();
    }
  }

  toggleHide() {
    this.hide = !this.hide;
  }

  onSubmit() {
    this.store.dispatch(new SystemActions.ClearSystemState());
    this.store.dispatch(new AuthActions.Authenticate(this.email.value, this.password.value));
  }

  private exchangeCodeForTokens(code: string): void {
    const codeVerifier = sessionStorage.getItem('code_verifier');
    const isSsoEnabled = this.cognitoSettings?.ssoEnabled ?? false;

    if (!this.isSsoLoginValid(this.enableAwsCognitoLogin, isSsoEnabled, codeVerifier)) {
      this.dispatchAuthenticationFailure();
      return;
    }

    this.showSpinner = true;

    this.authService.exchangeCodeForTokens(code, codeVerifier).pipe(
      finalize(() => {
        this.showSpinner = false;
      })
    ).subscribe({
      next: (jwtToken: IJwtoken) => {
        this.store.dispatch(new AuthActions.CognitoAuthenticate(jwtToken));
      },
      error: () => this.dispatchAuthenticationFailure()
    });
  }

  private isSsoLoginValid(enableAwsCognitoLogin: boolean, isSsoEnabled: boolean, codeVerifier: string | null): boolean {
    return enableAwsCognitoLogin && isSsoEnabled && codeVerifier != null;
  }

  private dispatchAuthenticationFailure(): void {
    this.store.dispatch(new AuthActions.AuthenticateFail("Single Sign-On Authentication Failed. Please try again or contact support if the issue persists."));
  }

  async initiateSamlAuth(): Promise<void> {
    const codeVerifier = this.utilitiesService.genRandomString(64);
    const hashed = await this.utilitiesService.sha256(codeVerifier);
    const codeChallenge = this.utilitiesService.base64URL(hashed);

    sessionStorage.setItem('code_verifier', codeVerifier);

    window.location.href = this.constructAuthUrl(codeChallenge);
  }

  private constructAuthUrl(codeChallenge: string): string {
    return `${this.cognitoSettings.cognitoUrl}` +
      `?identity_provider=${this.cognitoSettings.identityProvider}` +
      `&redirect_uri=${encodeURIComponent(this.cognitoSettings.redirectUrlSso)}` +
      `&response_type=code` +
      `&client_id=${this.cognitoSettings.cognitoClientId}` +
      `&scope=${encodeURIComponent('email openid phone')}` +
      `&code_challenge_method=S256` +
      `&code_challenge=${codeChallenge}`;
  }
}